import { jwtDecode } from 'jwt-decode';

const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

export interface ITokenClaims {
	userId: string | null;
	phoneNumber: string | null;
	name: string | null;
	role: string | null;
	roles: string[];
	activeContext: string | null;
	professionalId: string | null;
	businessId: string | null;
}

export const getRoleFromAccessToken = (token?: string | null): string | null => {
	if (!token) return null;

	try {
		const tokenData = jwtDecode<Record<string, unknown>>(token);
		return (tokenData?.[ROLE_CLAIM] as string) ?? (tokenData?.ActiveContext as string) ?? (tokenData?.role as string) ?? null;
	} catch {
		return null;
	}
};

export const getRolesFromAccessToken = (token?: string | null): string[] => {
	if (!token) return [];

	try {
		const tokenData = jwtDecode<Record<string, unknown>>(token);
		const rawRoles = tokenData?.Roles ?? tokenData?.roles;
		if (Array.isArray(rawRoles)) {
			return rawRoles.map(String).filter(Boolean);
		}
		if (typeof rawRoles === 'string') {
			return rawRoles
				.split(',')
				.map((r) => r.trim())
				.filter(Boolean);
		}
		const singleRole = (tokenData?.[ROLE_CLAIM] as string) ?? (tokenData?.ActiveContext as string) ?? (tokenData?.role as string);
		return singleRole ? [singleRole] : [];
	} catch {
		return [];
	}
};

export const getClaimsFromAccessToken = (token?: string | null): ITokenClaims => {
	if (!token) {
		return {
			userId: null,
			phoneNumber: null,
			name: null,
			role: null,
			roles: [],
			activeContext: null,
			professionalId: null,
			businessId: null,
		};
	}

	try {
		const tokenData = jwtDecode<Record<string, unknown>>(token);
		const role = (tokenData?.[ROLE_CLAIM] as string) ?? (tokenData?.ActiveContext as string) ?? (tokenData?.role as string) ?? null;
		const roles = getRolesFromAccessToken(token);
		const activeContext = (tokenData?.ActiveContext as string) ?? (tokenData?.activeContext as string) ?? role;
		const professionalId = (tokenData?.ProfessionalId as string) ?? (tokenData?.professionalId as string) ?? null;
		const businessId = (tokenData?.BusinessId as string) ?? (tokenData?.businessId as string) ?? null;
		const userId = (tokenData?.Id as string) ?? (tokenData?.UserId as string) ?? (tokenData?.userId as string) ?? null;
		const name = (tokenData?.Name as string) ?? (tokenData?.name as string) ?? null;
		const phoneNumber = (tokenData?.PhoneNumber as string) ?? (tokenData?.phoneNumber as string) ?? null;

		return {
			userId,
			phoneNumber,
			name,
			role,
			roles,
			activeContext,
			professionalId,
			businessId,
		};
	} catch {
		return {
			userId: null,
			phoneNumber: null,
			name: null,
			role: null,
			roles: [],
			activeContext: null,
			professionalId: null,
			businessId: null,
		};
	}
};

export const getDefaultRouteForRole = (role?: string | null) => {
	if (role === 'Business') {
		return { name: 'Index' };
	}
	if (role === 'Professional') {
		return { name: 'ProfessionalDashboard' };
	}
	return { name: 'Home' };
};

export const getDefaultRouteFromAccessToken = (token?: string | null) => getDefaultRouteForRole(getRoleFromAccessToken(token));
